import type { BandColumn, FormatTemplateInput } from './types';
import { numeracionVaEn } from './banda-rica';

/**
 * Encabezado y pie COMO BLOQUES (pedido de Andrey, 2 oct 2026: «que el
 * encabezado sea un bloque donde poner el texto y ajustarlo»). Debajo sigue
 * el modelo de siempre: hasta cuatro columnas de igual ancho en
 * headerColumns/footerColumns, que processing-ms pinta como una tabla. Aquí
 * solo se decide qué se ve como bloque y cómo se reescriben las columnas al
 * editar. Funciones puras, sin DOM: las usan la vista previa y el panel de
 * capas, así los dos editan exactamente el mismo estado.
 *
 * - Las columnas vacías no son bloques: no se ven ni se eligen. Mientras no se
 *   añada, borre, mueva o realinee nada en la banda se quedan donde estaban,
 *   y una plantilla sin tocar guarda exactamente lo mismo que antes.
 * - Añadir, borrar, mover o alinear reescribe la banda con `distribuir()`:
 *   las columnas salen de los bloques, y las vacías que hagan falta para que
 *   un bloque centrado quede en el centro de la hoja se generan ahí.
 * - Escribir, cambiar la imagen o su alto solo toca esa columna.
 * - Una plantilla plana (headerText/footerText sin columnas) se ve como un
 *   bloque de texto. Su texto, y la alineación del encabezado, se siguen
 *   guardando en el campo plano; lo que el modo plano no sabe guardar la pasa
 *   a columnas, como hacía «Construir por columnas».
 */

export type Banda = 'header' | 'footer';
export type TipoBloque = Exclude<BandColumn['type'], 'empty'>;

export const MAX_COLUMNAS = 4;
/** Topes del texto de una columna: los de `columnasLimpias()` en processing-ms. */
export const TEXTO_MAX = 300;
export const LINEAS_MAX = 4;
/** Alto personalizado de una imagen, en mm (el rango que acepta el backend). */
export const ALTO_IMG_MIN = 5;
export const ALTO_IMG_MAX = 60;

export interface Bloque {
  /** Posición en headerColumns/footerColumns; -1 = el texto plano (headerText/footerText). */
  indice: number;
  col: BandColumn;
}

type Valores = Pick<FormatTemplateInput,
  'headerText' | 'headerAlign' | 'footerText' | 'headerColumns' | 'footerColumns' |
  'headerEnabled' | 'footerEnabled' | 'pageNumbering' | 'pageNumberPosition'>;
type Cambio = Partial<FormatTemplateInput>;

const columnasCon = (banda: Banda, cols: BandColumn[]): Cambio =>
  (banda === 'header' ? { headerColumns: cols } : { footerColumns: cols });

const textoPlanoVacio = (v: Valores, banda: Banda): Cambio => {
  if (banda === 'header') return v.headerText ? { headerText: '' } : {};
  return v.footerText ? { footerText: '' } : {};
};

export function columnasDe(v: Valores, banda: Banda): BandColumn[] {
  return (banda === 'header' ? v.headerColumns : v.footerColumns) ?? [];
}

/** El texto plano visto como bloque. El pie plano sale centrado (`bandaPie`). */
function bloquePlano(v: Valores, banda: Banda, aunVacio = false): Bloque | null {
  const texto = banda === 'header' ? v.headerText : v.footerText;
  if (!texto && !aunVacio) return null;
  return { indice: -1, col: { type: 'text', text: texto ?? '', align: banda === 'header' ? (v.headerAlign ?? 'center') : 'center' } };
}

/**
 * Bloques de una banda, de izquierda a derecha. Solo las cuatro primeras
 * columnas cuentan, como en el backend. Con `planoVacio`, el texto plano
 * sigue siendo un bloque aunque se haya borrado entero: mientras se escribe
 * en él no debe desaparecer bajo el cursor.
 */
export function bloquesDe(v: Valores, banda: Banda, planoVacio = false): Bloque[] {
  const cols = columnasDe(v, banda);
  if (!cols.length) {
    const plano = bloquePlano(v, banda, planoVacio);
    return plano ? [plano] : [];
  }
  return cols.slice(0, MAX_COLUMNAS).map((col, indice) => ({ indice, col })).filter((b) => b.col.type !== 'empty');
}

/** Columnas sobre las que se edita: las que hay o, en una banda plana, su texto convertido en columna. */
function columnasBase(v: Valores, banda: Banda): BandColumn[] {
  const cols = columnasDe(v, banda);
  if (cols.length) return cols.slice(0, MAX_COLUMNAS);
  const plano = bloquePlano(v, banda);
  return plano ? [plano.col] : [];
}

export function nuevoBloque(tipo: TipoBloque, inicial?: Partial<BandColumn>): BandColumn {
  if (tipo === 'image') return { type: 'image', image: 'logo', size: 'm', align: 'center', ...inicial };
  if (tipo === 'pagenum') return { type: 'pagenum', align: 'right', ...inicial };
  return { type: 'text', text: '', align: 'center', ...inicial };
}

const ZONA = { left: 0, center: 1, right: 2 } as const;
const zona = (c: BandColumn): number => ZONA[c.align ?? 'center'];

/**
 * Columnas de una banda a partir de sus bloques, de izquierda a derecha.
 * processing-ms reparte la banda en columnas de igual ancho y alinea cada
 * bloque DENTRO de la suya, así que «centrado» solo cae en el centro de la
 * hoja si el bloque ocupa la columna del medio. Con dos bloques, uno
 * centrado y el otro a su lado ([izquierda, centro] o [centro, derecha]),
 * se reparten en tres columnas con una vacía en la zona libre; en los demás
 * casos, una columna por bloque. Determinista: los mismos bloques dan
 * siempre las mismas columnas.
 */
export function distribuir(bloques: BandColumn[]): BandColumn[] {
  if (bloques.length !== 2) return bloques;
  const [a, b] = bloques.map(zona);
  if (a >= b || (a !== ZONA.center && b !== ZONA.center)) return bloques;
  return [ZONA.left, ZONA.center, ZONA.right].map((z) => bloques.find((c) => zona(c) === z) ?? { type: 'empty', align: 'left' });
}

/** Los bloques sobre los que se edita, ya como columnas y sin las vacías. */
const bloquesBase = (v: Valores, banda: Banda): BandColumn[] => columnasBase(v, banda).filter((c) => c.type !== 'empty');

export function cabeOtroBloque(v: Valores, banda: Banda): boolean {
  return bloquesBase(v, banda).length < MAX_COLUMNAS;
}

/** El encabezado encendido sin texto ni columnas: `validar()` del backend no lo deja guardar. */
export const encabezadoVacio = (v: Valores): boolean =>
  v.headerEnabled && !v.headerText?.trim() && !(v.headerColumns ?? []).length;

export const tieneNumero = (v: Valores, banda: Banda): boolean => bloquesDe(v, banda).some((b) => b.col.type === 'pagenum');

/**
 * ¿La norma prohíbe poner una imagen en esta banda? processing-ms rechaza
 * guardar una imagen nueva si el formato bloquea headerImage/footerImage y la
 * banda no tenía ya una (`update()` de format-templates.service.ts).
 */
export const imagenBloqueada = (v: Valores, banda: Banda, bloqueados: readonly string[]): boolean =>
  bloqueados.includes(banda === 'header' ? 'headerImage' : 'footerImage')
  && !columnasDe(v, banda).some((c) => (c.type === 'image' && (c.image || c.imageUrl)) || (typeof c.richHtml === 'string' && /<img\b/i.test(c.richHtml)));

/**
 * Añade un bloque en la zona de su alineación (tras los que van más a la
 * izquierda): el número, que nace a la derecha, va al final; un texto
 * centrado, entre lo de la izquierda y lo de la derecha. Devuelve el cambio
 * y la columna que ocupa, o null si ya hay cuatro. Enciende la banda: una
 * banda visible solo por la numeración automática (pie apagado) no pintaría
 * el bloque nuevo.
 */
export function anadirBloque(v: Valores, banda: Banda, tipo: TipoBloque, inicial?: Partial<BandColumn>): { cambio: Cambio; indice: number } | null {
  const bloques = bloquesBase(v, banda);
  if (bloques.length >= MAX_COLUMNAS) return null;
  const nuevo = nuevoBloque(tipo, inicial);
  let pos = bloques.length;
  while (pos > 0 && zona(bloques[pos - 1]) > zona(nuevo)) pos--;
  const cols = distribuir([...bloques.slice(0, pos), nuevo, ...bloques.slice(pos)]);
  const encender: Cambio = banda === 'header'
    ? (v.headerEnabled ? {} : { headerEnabled: true })
    : (v.footerEnabled ? {} : { footerEnabled: true });
  return { cambio: { ...columnasCon(banda, cols), ...encender }, indice: cols.indexOf(nuevo) };
}

/** Cambia campos de un bloque. Si no cambia nada devuelve {} (no convierte una banda plana por un clic de más). */
export function editarBloque(v: Valores, banda: Banda, indice: number, cambio: Partial<BandColumn>): Cambio {
  const cols = columnasDe(v, banda);
  const bloque = indice < 0
    ? (cols.length ? null : bloquePlano(v, banda, true))
    : (cols[indice] ? { indice, col: cols[indice] } : null);
  if (!bloque) return {};
  const actual = bloque.col;
  if ((Object.keys(cambio) as (keyof BandColumn)[]).every((k) => actual[k] === cambio[k])) return {};
  if (indice >= 0) {
    return columnasCon(banda, cols.map((c, i) => (i === indice ? { ...c, ...cambio } : c)));
  }
  const claves = Object.keys(cambio);
  if (banda === 'header' && claves.every((k) => k === 'text' || k === 'align')) {
    return {
      ...('text' in cambio ? { headerText: cambio.text ?? '' } : {}),
      ...(cambio.align ? { headerAlign: cambio.align } : {}),
    };
  }
  if (banda === 'footer' && claves.every((k) => k === 'text')) return { footerText: cambio.text ?? '' };
  return columnasCon(banda, [{ ...bloque.col, ...cambio }]);
}

/**
 * Alinea un bloque y vuelve a repartir la banda, para que «centro» sea el
 * centro de la hoja. Devuelve la columna nueva del bloque, o null si ya
 * tenía esa alineación (un clic de más no toca el estado).
 */
export function alinearBloque(v: Valores, banda: Banda, indice: number, align: NonNullable<BandColumn['align']>): { cambio: Cambio; indice: number } | null {
  if (indice < 0) {
    const cambio = editarBloque(v, banda, -1, { align });
    if (!Object.keys(cambio).length) return null;
    // El pie plano no guarda alineación: pasa a columnas y el bloque es la 0.
    return { cambio, indice: 'headerColumns' in cambio || 'footerColumns' in cambio ? 0 : -1 };
  }
  const bloques = bloquesDe(v, banda);
  const b = bloques.find((x) => x.indice === indice);
  if (!b || (b.col.align ?? 'center') === align) return null;
  const nuevo: BandColumn = { ...b.col, align };
  const cols = distribuir(bloques.map((x) => (x === b ? nuevo : x.col)));
  return { cambio: columnasCon(banda, cols), indice: cols.indexOf(nuevo) };
}

/**
 * Borra un bloque y vuelve a repartir los demás. Si la banda se queda sin
 * bloques se borra también su texto plano: con las columnas vacías el
 * backend vuelve al texto plano, y el texto borrado reaparecería (las
 * plantillas argentinas guardan los dos).
 */
export function borrarBloque(v: Valores, banda: Banda, indice: number): Cambio {
  if (indice < 0) return textoPlanoVacio(v, banda);
  const cols = distribuir(bloquesDe(v, banda).filter((b) => b.indice !== indice).map((b) => b.col));
  return { ...columnasCon(banda, cols), ...(cols.length ? {} : textoPlanoVacio(v, banda)) };
}

/** Mueve el bloque de la posición `desde` a la `hasta` (posiciones en `bloquesDe`). Devuelve su columna nueva. */
export function reordenarBloques(v: Valores, banda: Banda, desde: number, hasta: number): { cambio: Cambio; indice: number } | null {
  const bloques = bloquesDe(v, banda);
  if (desde === hasta || desde < 0 || hasta < 0 || desde >= bloques.length || hasta >= bloques.length) return null;
  if (bloques[desde].indice < 0) return null;
  const orden = [...bloques];
  const [movido] = orden.splice(desde, 1);
  orden.splice(hasta, 0, movido);
  const cols = distribuir(orden.map((b) => b.col));
  return { cambio: columnasCon(banda, cols), indice: cols.indexOf(movido.col) };
}

/** Mueve un bloque un puesto a la izquierda (-1) o a la derecha (+1). */
export function moverBloque(v: Valores, banda: Banda, indice: number, hacia: -1 | 1): { cambio: Cambio; indice: number } | null {
  const pos = bloquesDe(v, banda).findIndex((b) => b.indice === indice);
  if (pos < 0) return null;
  return reordenarBloques(v, banda, pos, pos + hacia);
}

/**
 * ¿La banda lleva además el número automático de «Numeración de páginas»?
 * Es la línea que el backend añade cuando ninguna columna de la banda es el
 * número (`bandaEncabezado`/`bandaPie`). Con un token de número en una banda
 * rica encendida no hay automático en ninguna (`numeracionVaEn`); sin bandas
 * ricas es la condición de siempre.
 */
export function numeroAutomaticoEn(v: Valores, banda: Banda): boolean {
  return numeracionVaEn(v) === banda && !columnasDe(v, banda).some((c) => c.type === 'pagenum');
}

/** Alto de la imagen en mm, con la misma regla que el backend: heightMm válido manda sobre size. */
export function altoImagenMm(c: BandColumn): number {
  if (typeof c.heightMm === 'number' && c.heightMm >= ALTO_IMG_MIN && c.heightMm <= ALTO_IMG_MAX) return c.heightMm;
  return c.size === 'l' ? 22 : c.size === 's' ? 10 : 15;
}

export const ajustarAltoImagen = (mm: number): number =>
  Math.max(ALTO_IMG_MIN, Math.min(ALTO_IMG_MAX, Math.round(mm)));

/**
 * Texto de un bloque tal como se guarda. En columna admite hasta cuatro
 * renglones y 300 caracteres (los topes del backend); el texto plano es de
 * una sola línea, como su campo de siempre.
 */
export function limpiarTexto(texto: string, multilinea: boolean): string {
  const s = texto.replace(/\r\n?/g, '\n').replace(/ /g, ' ');
  if (!multilinea) return s.replace(/\n+/g, ' ');
  const lineas = s.split('\n');
  return (lineas.length > LINEAS_MAX ? lineas.slice(0, LINEAS_MAX).join('\n') : s).slice(0, TEXTO_MAX);
}

/*
 * TEXTO CON FORMATO de un bloque (pedido de Andrey, 3 oct 2026: negrita,
 * cursiva y subrayado en el encabezado y el pie, con el mismo editor de la
 * fórmula de cierre). Contrato con processing-ms: `html` solo lleva <strong>,
 * <em>, <u> y <br>, sin atributos, hasta 4 renglones y 1.000 caracteres, y
 * `text` se guarda siempre al lado como texto plano. Aquí se normaliza lo que
 * produzca el editor (o un pegado desde Word o una web) a esa forma canónica.
 * Sin DOM a propósito: es la misma función en el navegador y en los tests, y
 * su salida ya es segura (el texto sale escapado y no hay más etiquetas).
 */

/** Tope del HTML de un bloque: el que aplica processing-ms al guardar. */
export const HTML_MAX = 1000;

export interface TextoConFormato {
  /** Solo <strong>, <em>, <u> y <br>; null si el texto no lleva formato. */
  html: string | null;
  /** El mismo texto sin etiquetas: <br> → \n, entidades decodificadas. */
  text: string;
}

const MARCAS = ['strong', 'em', 'u'] as const;
type Marca = (typeof MARCAS)[number];
const BIT: Record<Marca, number> = { strong: 1, em: 2, u: 4 };

// Delimitan renglones aunque no sean <br>: TinyMCE envuelve en <p>, y un
// pegado trae <div>, <li>, <h1>, filas de tabla…
const BLOQUE = /^(?:p|div|li|ul|ol|dl|dt|dd|h[1-6]|blockquote|pre|table|thead|tbody|tfoot|tr|td|th|section|article|header|footer|address|figure|figcaption|hr)$/;
const VACIOS = new Set(['img', 'input', 'meta', 'link', 'wbr', 'col', 'area', 'base', 'source', 'track', 'embed', 'param']);
// Comentario | elemento cuyo contenido no es texto | etiqueta | <!…> o <?…> | «<» suelto | texto.
const TOKEN = /<!--[\s\S]*?(?:-->|$)|<(script|style|template|textarea|title|head|iframe|object|noscript|svg|math)\b(?:"[^"]*"|'[^']*'|[^'">])*>[\s\S]*?(?:<\/\1\s*>|$)|<(\/?)([a-zA-Z][\w:-]*)((?:"[^"]*"|'[^']*'|[^'">])*)>|<[!?][^>]*>|<|[^<]+/gi;
const ENTIDADES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0' };
// Las variables no se parten: {traductor} con media palabra en negrita no lo
// reconocería sustituirVariables, que busca el token entero dentro del HTML.
const VARIABLE = /\{[^{}\s<>]{1,40}\}/g;

function decodificar(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);?/gi, (todo, e: string) => {
    if (e[0] === '#') {
      const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : todo;
    }
    return ENTIDADES[e.toLowerCase()] ?? todo;
  });
}

const escaparHtml = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Marcas que pone o quita un elemento: la etiqueta y, por encima, su estilo (los pegados de Docs llegan como <span style>). */
function marcasDe(tag: string, attrs: string): { pone: number; quita: number } {
  let pone = tag === 'strong' || tag === 'b' ? BIT.strong : tag === 'em' || tag === 'i' ? BIT.em : tag === 'u' || tag === 'ins' ? BIT.u : 0;
  let quita = 0;
  const estilo = /\bstyle\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(attrs);
  const css = (estilo?.[1] ?? estilo?.[2] ?? '').toLowerCase();
  const regla = (prop: string) => new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]*)`).exec(css)?.[1]?.trim();
  const peso = regla('font-weight');
  if (peso) { if (/^(?:bold|bolder|[6-9]00)$/.test(peso)) pone |= BIT.strong; else quita |= BIT.strong; }
  const estiloLetra = regla('font-style');
  if (estiloLetra) { if (/^(?:italic|oblique)/.test(estiloLetra)) pone |= BIT.em; else quita |= BIT.em; }
  const deco = regla('text-decoration-line') ?? regla('text-decoration');
  if (deco) { if (/underline/.test(deco)) pone |= BIT.u; else if (/^none/.test(deco)) quita |= BIT.u; }
  return { pone: pone & ~quita, quita };
}

/** Las marcas, siempre en el mismo orden y bien anidadas; cada renglón cierra las suyas antes del <br>. */
function serializar(t: string, m: number[]): string {
  let out = '';
  const abiertas: Marca[] = [];
  const cerrar = (hasta: number) => { while (abiertas.length > hasta) out += `</${abiertas.pop()}>`; };
  for (let i = 0; i < t.length; i++) {
    if (t[i] === '\n') { cerrar(0); out += '<br>'; continue; }
    const quiero = MARCAS.filter((k) => m[i] & BIT[k]);
    let comun = 0;
    while (comun < abiertas.length && comun < quiero.length && abiertas[comun] === quiero[comun]) comun++;
    cerrar(comun);
    for (const k of quiero.slice(comun)) { out += `<${k}>`; abiertas.push(k); }
    out += escaparHtml(t[i]);
  }
  cerrar(0);
  return out;
}

/**
 * Forma canónica del texto de un bloque a partir del HTML del editor (o de
 * cualquier HTML: lo que no sea negrita, cursiva, subrayado o salto se queda
 * en su texto). b→strong, i→em, subrayado por estilo→u, párrafos y divs→<br>.
 * Los blancos se tratan como los pinta el navegador (y como escaparBanda, que
 * recorta cada renglón). Topes: los de la columna (4 renglones y 300
 * caracteres de texto) y HTML_MAX; si el HTML no cabe se queda solo el texto.
 * Con `multilinea` en false (el texto plano del encabezado o del pie), todo
 * va en un renglón, como en su campo de siempre. Con `topes` en false no se
 * recorta nada: el editor lo usa para saber si un pegado se pasó.
 */
export function normalizarTextoBloque(html: string | null | undefined, multilinea = true, topes = true): TextoConFormato {
  const lineas: { t: string; m: number[] }[] = [{ t: '', m: [] }];
  const pila: { tag: string; pone: number; quita: number }[] = [];
  let marcas = 0;
  const recalcular = () => { marcas = pila.reduce((m, e) => (m | e.pone) & ~e.quita, 0); };
  const actual = () => lineas[lineas.length - 1];
  for (const tok of String(html ?? '').matchAll(TOKEN)) {
    const [todo, crudo, barra, nombre, attrs = ''] = tok;
    if (crudo || todo.startsWith('<!--')) continue;
    if (nombre) {
      const tag = nombre.toLowerCase();
      if (tag === 'br') {
        if (!barra) lineas.push({ t: '', m: [] });
      } else if (BLOQUE.test(tag)) {
        if (actual().t) lineas.push({ t: '', m: [] });
      } else if (barra) {
        const i = pila.map((e) => e.tag).lastIndexOf(tag);
        if (i >= 0) { pila.splice(i, 1); recalcular(); }
      } else if (!VACIOS.has(tag) && !/\/\s*$/.test(attrs)) {
        pila.push({ tag, ...marcasDe(tag, attrs) });
        recalcular();
      }
      continue;
    }
    if (todo.length > 1 && todo[0] === '<') continue;
    // Los saltos del código fuente entre </p> y <p> no son texto.
    if (/^[ \t\r\n\f]*[\r\n\t\f][ \t\r\n\f]*$/.test(todo)) continue;
    const s = decodificar(todo)
      .replace(/[\u0000-\u0008\u000b\u000e-\u001f\u007f]/g, '')
      .replace(/\s/g, ' ');
    const l = actual();
    l.t += s;
    for (let i = 0; i < s.length; i++) l.m.push(marcas);
  }
  // Cada renglón sin blancos repetidos ni en los bordes.
  const limpias = lineas.map(({ t, m }) => {
    let rt = '';
    const rm: number[] = [];
    for (let i = 0; i < t.length; i++) {
      if (t[i] === ' ' && (!rt || rt.endsWith(' '))) continue;
      rt += t[i];
      rm.push(m[i]);
    }
    if (rt.endsWith(' ')) { rt = rt.slice(0, -1); rm.pop(); }
    return { t: rt, m: rm };
  });
  let partes = multilinea ? limpias : [limpias.filter((l) => l.t).reduce((a, l) => (a.t ? { t: `${a.t} ${l.t}`, m: [...a.m, 0, ...l.m] } : l), { t: '', m: [] as number[] })];
  // Después del tope: si el cuarto renglón queda vacío, no se guarda un salto de más.
  if (topes) partes = partes.slice(0, LINEAS_MAX);
  while (partes.length > 1 && !partes[partes.length - 1].t) partes = partes.slice(0, -1);
  let t = partes.map((l) => l.t).join('\n');
  let m = partes.flatMap((l, i) => (i ? [0, ...l.m] : l.m));
  for (const v of t.matchAll(VARIABLE)) {
    const desde = v.index ?? 0;
    const tramo = m.slice(desde, desde + v[0].length);
    const veces = new Map<number, number>();
    for (const x of tramo) veces.set(x, (veces.get(x) ?? 0) + 1);
    const mayoria = [...veces.entries()].reduce((a, b) => (b[1] > a[1] ? b : a))[0];
    for (let i = desde; i < desde + v[0].length; i++) m[i] = mayoria;
  }
  const conFormato = () => m.some((x, i) => x !== 0 && t[i] !== ' ' && t[i] !== '\n');
  // El texto plano de siempre no tenía tope (limpiarTexto); con formato pasa
  // a columna, y ahí manda el de la columna.
  if (topes && t.length > TEXTO_MAX && (multilinea || conFormato())) {
    // Sin partir un emoji (par sustituto) por la mitad.
    const corte = /[\ud800-\udbff]/.test(t[TEXTO_MAX - 1]) ? TEXTO_MAX - 1 : TEXTO_MAX;
    t = t.slice(0, corte).replace(/[ \n]+$/, '');
    m = m.slice(0, t.length);
  }
  if (!conFormato()) return { html: null, text: t };
  const salida = serializar(t, m);
  return { html: salida.length <= HTML_MAX ? salida : null, text: t };
}

/** Texto plano de un HTML de bloque. */
export const textoDeHtml = (html: string | null | undefined): string => normalizarTextoBloque(html).text;

/** El texto plano como HTML de bloque, para abrirlo en el editor. */
export const htmlDeTexto = (texto: string | null | undefined): string =>
  String(texto ?? '').replace(/\r\n?/g, '\n').split('\n').map(escaparHtml).join('<br>');

/**
 * Lo que se pinta de un bloque de texto con formato: su HTML ya canónico (la
 * vista previa lo pasa además por DOMPurify), o null si no lleva formato y
 * basta su texto, como en una plantilla vieja.
 */
export const htmlDeBloque = (col: Pick<BandColumn, 'html'>): string | null =>
  (col.html ? normalizarTextoBloque(col.html).html : null);

/**
 * Cambio de un bloque al escribir en el editor con formato. Sin formato no se
 * añade `html`: la columna (o el texto plano del encabezado) guarda lo mismo
 * que antes. Si tenía formato y se quitó entero, `html` se vacía.
 */
export function cambioDeTexto(col: Pick<BandColumn, 'html'> | undefined, valor: TextoConFormato): Partial<BandColumn> {
  if (valor.html) return { text: valor.text, html: valor.html };
  return col?.html != null ? { text: valor.text, html: null } : { text: valor.text };
}
